import { ReactNode, useState } from 'react'
import styled, { css } from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import ConfettiExplosion from 'react-confetti-explosion'
import { useSelector } from 'react-redux'
import { Box } from '../../../UI/Box'
import { theseus } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { Checkbox } from '../../../../page_components/DropDetails/ChapterView'
import TaskBody from '../../TaskBody'
import { SubTask } from '../../../../page_components/PoolDetails/Missions/types'
import AnimatedLoader from '../../../AnimatedLoader'
import { ThemedButton } from '../../../ThemedButton'

const CheckContainer = styled(Box)<{ isVisible: boolean }>`
  max-width: 0px;
  overflow: hidden;
  opacity: 0;
  transition: 0.3s ease-in-out;
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      max-width: 28px;
      padding-right: 8px;
    `}
`

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

const OnChainTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const { publicName, instructions } = subtask
  const title = publicName ?? 'Follow us on X'
  const description =
    instructions ?? 'Simply press the follow button below to get our updates'

  const [isLoadingVerify, setIsLoadingVerify] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const project = useSelector(selectCurrentProject)

  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)

  const updateSearchParam = (paramName: string, value: string) => {
    searchParams.set(paramName, value)
    history.push(`${location.pathname}?${searchParams.toString()}`)
  }

  function selfDestruct(ms = 2000) {
    setTimeout(() => {
      updateSearchParam('active_mission', '')
    }, ms)
  }

  async function validate() {
    try {
      setError(null)
      setIsLoadingVerify(true)

      const { data } = await theseus.post(`/task-submission/b2c/on-chain`, {
        subtaskId: subtask.id,
        projectId: project.id,
        taskId,
      })
      if (!data?.message)
        throw new Error(
          "We couldn't verify your submission - please ensure you meet the criteria",
        )
      const { message } = data
      if (!message) throw new Error('Bad response')
      if (message !== 'success') throw new Error(message)
      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error?.message)
      setError(error.message)
    } finally {
      setIsLoadingVerify(false)
    }
  }

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={
        error && error === 'No user to follow'
          ? "You've hammered the Discord API pretty hard! Love the effort but give it a few minutes to cool down!"
          : error
      }
      extra={taskAmount}
      actions={
        <Box fu aw>
          <ThemedButton
            onClick={validate}
            disabled={isComplete || isLoadingVerify}
            $styleType="primary"
          >
            {isComplete && <ConfettiExplosion zIndex={10000} />}
            <CheckContainer isVisible={isComplete}>
              <Checkbox />
            </CheckContainer>
            {isLoadingVerify ? (
              <AnimatedLoader size={14} />
            ) : isComplete ? (
              'Verified'
            ) : (
              'Verify'
            )}
          </ThemedButton>
        </Box>
      }
    />
  )
}

export default OnChainTask
